'use client'

import {
  BottomSheet,
  Button,
  Cell,
  Container,
  Dialog,
  Divider,
  InputTextArea,
  Navigation,
  Rating,
  SelectionGroup,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { useEffect, useRef, useState } from 'react'

import { X24 } from '@vinted/monochrome-icons'

import {
  submitSupportFeedback,
  chatbotFeedbackV2RequestViewed,
  autoreplyFeedbackV2RequestViewed,
  chatbotFeedbackV2RequestClicked,
  autoreplyFeedbackV2RequestClicked,
  chatbotFeedbackV2FormSubmitted,
  autoReplyFeedbackV2FormSubmitted,
  chatbotFeedbackV2FormViewed,
  autoReplyFeedbackV2FormViewed,
} from '@marketplace-web/domain/automated-support-feedback-data'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { autoreplyFeedbackClosed, chatbotFeedbackClosed } from '_libs/common/event-tracker/events'

import useHelpCenterSessionTracking from 'hooks/useHelpCenterSessionTracking'

import { FEEDBACK_REQUEST_MESSAGE_TYPES, getKeyPrefix } from './utils'

type FeedbackRequestMessageProps = {
  entityType: string
}

const FeedbackRequestMessage = (props: FeedbackRequestMessageProps) => {
  const { entityType } = props

  const breakpoints = useBreakpoint()
  const translate = useTranslate('')
  const { track } = useTracking()
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { conversation } = useConversationContext()
  const userId = useSession().user?.id
  const conversationId = conversation?.id
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const [isExpandedFormVisible, setIsExpandedFormVisible] = useState<boolean>(false)
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false)
  const [rating, setRating] = useState<number>(0)
  const [feedback, setFeedback] = useState<string>('')
  const [isIssueResolved, setIsIssueResolved] = useState<boolean | null>(null)

  useEffect(() => {
    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackV2RequestViewed(eventParams))
    } else {
      track(chatbotFeedbackV2RequestViewed(eventParams))
    }
  }, [conversationId, entityType, helpCenterSessionId, track])

  const handleSubmit = () => {
    if (!userId || !conversationId || !helpCenterSessionId) return
    setIsSubmittingForm(true)

    const trackSubmitParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating,
      ...(inputRef.current?.value !== '' && { comment: inputRef.current?.value }),
      ...(isIssueResolved !== null && { issueResolved: isIssueResolved }),
    }

    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoReplyFeedbackV2FormSubmitted(trackSubmitParams))
    } else {
      track(chatbotFeedbackV2FormSubmitted(trackSubmitParams))
    }

    submitSupportFeedback({ userId, conversationId }).finally(() => {
      setIsSubmittingForm(false)
      setIsExpandedFormVisible(false)
      setRating(0)
      setFeedback('')
      setIsIssueResolved(null)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    })
  }

  const handleDismiss = () => {
    if (!helpCenterSessionId || !conversationId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackClosed(eventParams))
    } else {
      track(chatbotFeedbackClosed(eventParams))
    }
    setIsExpandedFormVisible(false)
    setRating(0)
    setFeedback('')
    setIsIssueResolved(null)
  }

  const handleRatingSelect = (val: number) => {
    setRating(val)
  }

  const handleFormExpansion = (val: number) => {
    handleRatingSelect(val)
    setIsExpandedFormVisible(true)

    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating: val,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoReplyFeedbackV2FormViewed(eventParams))
    } else {
      track(chatbotFeedbackV2FormViewed(eventParams))
    }
  }

  const trackRatingClick = (val: number) => {
    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating: val,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackV2RequestClicked(eventParams))
    } else {
      track(chatbotFeedbackV2RequestClicked(eventParams))
    }
  }

  const FeedbackRating = ({ handleRatingSelection }) => {
    return (
      <div className="request-message__header">
        <Divider />
        <Spacer size={Spacer.Size.Large} />
        <Text
          as="h2"
          text={translate(`${getKeyPrefix(entityType)}.title`)}
          type={Text.Type.SectionHeading}
        />
        <Cell
          styling={Cell.Styling.Narrow}
          body={
            <Text
              as="h2"
              type={Text.Type.Subtitle}
              text={translate(`${getKeyPrefix(entityType)}.subtitle`)}
            />
          }
        />
        <Spacer />
        <div className="u-flexbox u-justify-content-center u-align-items-center">
          <Rating
            aria={{
              'aria-label': 'Rating',
            }}
            isInteractive
            name="star-rating"
            onSelect={val => {
              handleRatingSelection(val)
              trackRatingClick(val)
            }}
            value={rating}
            size={Rating.Size.Large}
            testId="feedback-request-star-rating"
          />
        </div>
        <Spacer size={Spacer.Size.Large} />
      </div>
    )
  }
  const FeedbackForm = () => {
    return (
      <Container>
        <Text
          as="h4"
          type={Text.Type.Subtitle}
          text={translate(`${getKeyPrefix(entityType)}.question`)}
        />
        <SelectionGroup direction={SelectionGroup.Direction.Horizontal} styling="default">
          <SelectionGroup.SelectionItem
            width="120px"
            isSelected={isIssueResolved === true}
            key="resolved"
            body={translate(`${getKeyPrefix(entityType)}.resolved_button`)}
            showSelectionIndicator={false}
            onClick={() => setIsIssueResolved(true)}
          />
          <SelectionGroup.SelectionItem
            showSelectionIndicator={false}
            width="120px"
            isSelected={isIssueResolved === false}
            key="not-resolved"
            onClick={() => setIsIssueResolved(false)}
            body={translate(`${getKeyPrefix(entityType)}.not_resolved_button`)}
          />
        </SelectionGroup>
        <Divider />
        <InputTextArea
          name="feedback-request-message"
          placeholder={translate(`${getKeyPrefix(entityType)}.follow_up_question`)}
          defaultValue={feedback}
          uncontrolled
          onBlur={() => setFeedback(inputRef.current?.value || '')}
          ref={inputRef}
          testId="feedback-request-form"
        />
        <Spacer />
        <Button
          testId="fcr-form-submit-button"
          styling={Button.Styling.Filled}
          text={translate(`${getKeyPrefix(entityType)}.submit_button`)}
          theme="primary"
          onClick={handleSubmit}
          isLoading={isSubmittingForm}
        />
        <Divider />
      </Container>
    )
  }

  const FeedbackFormDialog = ({ isVisible, handleFormDismiss }) => {
    return (
      <Dialog show={isVisible} enableCloseButton contentDimensions={{ width: 'max-content' }}>
        <Navigation
          theme="transparent"
          right={
            <Button styling={Button.Styling.Flat} onClick={handleFormDismiss} iconName={X24} />
          }
        />
        <FeedbackRating handleRatingSelection={handleRatingSelect} />
        <Spacer size={Spacer.Size.Medium} />
        <Divider />
        <FeedbackForm />
      </Dialog>
    )
  }

  return (
    <Container styling={Container.Styling.Narrow}>
      <FeedbackRating handleRatingSelection={handleFormExpansion} />
      {breakpoints.phones ? (
        <BottomSheet isVisible={isExpandedFormVisible} onClose={handleDismiss}>
          <Navigation
            theme="transparent"
            right={<Button styling={Button.Styling.Flat} onClick={handleDismiss} iconName={X24} />}
          />
          <FeedbackRating handleRatingSelection={handleRatingSelect} />
          <Divider />
          <FeedbackForm />
        </BottomSheet>
      ) : (
        <FeedbackFormDialog handleFormDismiss={handleDismiss} isVisible={isExpandedFormVisible} />
      )}
    </Container>
  )
}

export default FeedbackRequestMessage
